import * as amplitude from "@amplitude/analytics-browser";
import {Experiment} from "@amplitude/experiment-js-client";

const TAG = "AmpliSDK";

export let initialized = false;
export let experiment = {};

// Promise tracking the in-flight initAmplitude() call. Cached so:
//  - Re-entrant initAmplitude() calls share a single init.
//  - getAmplitudeExperiment() can await it before reading variants, so pre-login
//    experiments (e.g. kmp-twilio-voice-support on the phone-entry screen) don't
//    race against the SDK's anonymous-user fetch.
let initPromise = null;

export function initAmplitude(apiKey, experimentDeployment) {
    if (initPromise) return initPromise;
    initPromise = (async () => {
        try {
            if (initialized) {
                console.warn(TAG, "Amplitude is initialized already!");
                return;
            }
            console.info(TAG, "Init amplitude");
            const analytics = amplitude.init(apiKey, {
                autocapture: false,
            });
            await analytics;
            experiment = Experiment.initializeWithAmplitudeAnalytics(experimentDeployment);
            // Fetch variants for the current (anonymous, device-id-only) user so
            // experiments that gate pre-login UI resolve to their bucketed value
            // on the first read. identifyAmplitudeUser() will re-fetch with the
            // authenticated identity after sign-in. Matches Android's
            // ExperimentManagerAndroid.initializeExperiments(), which calls
            // client.start() (which auto-fetches).
            if (typeof experiment.fetch === "function") {
                try {
                    console.info(TAG, "Fetching anonymous experiments");
                    await experiment.fetch();
                    console.info(TAG, "Anonymous experiments fetched");
                } catch (fetchError) {
                    console.warn(TAG, "Anonymous experiments fetch failed", fetchError);
                }
            }
            initialized = true;
            console.info(TAG, "Init amplitude - complete, experiment initialized:", experiment);
        } catch (error) {
            console.error(TAG, error);
        }
    })();
    return initPromise;
}

export async function identifyAmplitudeUser(userId, options = {}) {
    try {
        console.info(TAG, "Identify amplitude user", userId, options);

        const identifyEvent = new amplitude.Identify();
        Object.keys(options).forEach((key) => {
            identifyEvent.set(key, options[key]);
        });

        amplitude.setUserId(userId);
        amplitude.identify(identifyEvent);

        if (experiment && typeof experiment.fetch === "function") {
            console.info(TAG, "Fetching experiments for user", userId);
            await experiment.fetch();
            console.info(TAG, "Experiments fetched successfully");
        } else {
            console.warn(TAG, "Experiment not initialized yet, skipping experiment fetch");
        }
    } catch (error) {
        console.error(TAG, error);
    }
}

export function trackEvent(event) {
    console.log(TAG, `Track ${event.event_type}`, event);
    try {
        amplitude.track(event);
    } catch (error) {
        console.error(TAG, error);
    }
}

export async function getAmplitudeExperiment(name) {
    // Wait for the in-flight init (anonymous fetch included) so we don't read a
    // pre-fetch fallback variant and then have it cached upstream in
    // WebExperimentManager's TimedLruCache for the rest of the session.
    if (initPromise) {
        await initPromise;
    }
    if (!initialized || !experiment || typeof experiment.variant !== "function") {
        console.warn(TAG, "Experiment not initialized, returning empty variant for", name);
        return new Variant(name, null);
    }
    const variant = experiment.variant(name);
    console.log(TAG, "fetched experiment", name, variant, variant?.payload);
    return new Variant(name, variant?.payload);
}

export async function allAmplitudeExperiments() {
    if (initPromise) {
        await initPromise;
    }
    if (!initialized || !experiment || typeof experiment.all !== "function") {
        console.warn(TAG, "Experiment not initialized, returning empty array");
        return [];
    }
    console.log(TAG, "Fetch all experiments");
    const experiments = experiment.all();
    const array = Object.entries(experiments)
        .filter(([key, _]) => key != null)
        .map(([key, value]) => new Variant(key, value?.payload));
    console.log(TAG, "Fetched all experiments", array);
    return array;
}

export class Variant {
    constructor(key, payload) {
        this.key = key;
        if (payload == null) {
            this.payload = null;
        } else if (payload instanceof String) {
            this.payload = payload;
        } else {
            this.payload = JSON.stringify(payload);
        }
    }
}

export class AmpliEvent {
    constructor(event_type, event_properties, groups = {}, group_properties = {}) {
        this.event_type = event_type;
        this.event_properties = event_properties;
        this.groups = groups;
        this.group_properties = group_properties;
    }
}
